import { DEFAULT_CONTENT_LOCALE } from '@course-authoring/shared-types';
import { useClientContext, toScopeAccountId } from './useClientContext';

export interface CourseClientScope {
  /** Client account used by the English-only general course write path. */
  generalWriteAccountId: number | null;
  /** Whether the English-only general course write path is client-scoped. */
  isGeneralWriteClientContext: boolean;
  /** Active client retained for focused metadata in every locale. */
  metadataAccountId: number | null;
  /** Active client retained for account-resolved Preview in every locale. */
  previewAccountId: number | null;
  /**
   * Stable key for the editor's content hydration / autosave baseline. Switching
   * course, locale, or client account changes it so local state reseeds and the
   * autosave baseline resets (preventing a cross-account dirty save).
   */
  hydrationScopeKey: string;
}

/**
 * Derives the course editor's client-context scope from the active client
 * context and selected locale — the course mirror of useLessonClientScope.
 * General course editing remains en-only in client context. Focused metadata
 * and Preview retain the selected account in every locale.
 */
export function useCourseClientScope(
  courseId: string | undefined,
  selectedLocale: string
): CourseClientScope {
  const { clientContext } = useClientContext();
  const metadataAccountId = toScopeAccountId(clientContext?.id);
  const generalWriteAccountId = selectedLocale === DEFAULT_CONTENT_LOCALE
    ? metadataAccountId
    : null;
  return {
    generalWriteAccountId,
    isGeneralWriteClientContext: generalWriteAccountId !== null,
    metadataAccountId,
    previewAccountId: metadataAccountId,
    hydrationScopeKey: `${courseId ?? ''}:${selectedLocale}:${metadataAccountId ?? 'base'}`,
  };
}
